import * as React from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import * as DateHelpers from './DateHelpers';
import Pager from './Paging';
import { Link } from 'react-router-dom';
import type { InvoiceDetails, InvoiceItems } from './models/InvoiceInterfaces';
import type { GenericList } from './models/JobSharedInterfaces';
import ConfirmPopup from './ConfirmPopup';
import InvoicingPaidDateModal from './InvoicingPaidDateModal';
import { GetQuery, PostQuery } from './Shared';

interface InvoiceProps {
    invoiceDetails: InvoiceDetails[];
    invoiceTotalFormatted: string;
    showCustType: boolean;
    showJobInfo: boolean;
    showQuoteNo: boolean;
    showHirePeriod: boolean;
    showStatus: boolean;
    showStartDate: boolean;     //min hire period start date - used for events
    showPaidDate: boolean;
    showPaid: boolean;
    showSend: boolean;
    showRemove: boolean;
    showSentTo: boolean;
    showReckonInvNo: boolean;
    showError: boolean;
    showSent: boolean;
    canEdit: boolean;
    canEditHirePeriod: boolean;
    canSend: boolean;
    canUpdatePaid: boolean;
    page: number;
    pageSize: number;
    totalRecords: number;
    pageChanged(page: number): void;
    beforeValid(updatedInvoices: InvoiceDetails[]): void;
    afterValid(updatedInvoices: InvoiceDetails[]): void;
    saveHirePeriod(updatedInvoices: InvoiceDetails[]): void;
}

interface ItemList {
    id: number;
    name: string;
    companyId: number;
    typeId: number;
    chargeGst: boolean;
}

const InvoiceListData = (props: InvoiceProps) => {
    const [gst, setGst] = React.useState(0.1);
    const [itemList, setItemList] = React.useState<ItemList[]>([]);
    const [itemTypes, setItemTypes] = React.useState<GenericList[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [modalType, setModalType] = React.useState(0);   //1=Cancel, 2=Send, 3=Paid, 4=Remove, 5=Delete Item
    const [showDateModal, setShowDateModal] = React.useState(false);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [showSentModal, setShowSentModal] = React.useState(false);
    const [modalDate, setModalDate] = React.useState("");
    const [invoiceIndex, setInvoiceIndex] = React.useState(0);
    const [invoiceItemIndex, setInvoiceItemIndex] = React.useState(0);
    const [invoiceNumber, setInvoiceNumber] = React.useState(0);
    const [qbinvoicenumber, setQbInvoiceNumber] = React.useState(0);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [canResend, setCanResend] = React.useState(false);    //so I can resend invoices to reckon

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "GetInvoiceItemTypes";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setItemList(result.data.items);
            setItemTypes(result.data.itemTypes);
            setGst(result.data.gst);
            setCanResend(result.data.canResend)
            setLoading(false);
        }
    }

    const updateShowDetail = (rowIndex: number, showDetail: boolean) => {
        //update show Detail from False to True and vice versa
        if (showDetail == null) {
            showDetail = true;
        } else {
            showDetail = !showDetail;
        }

        //update the row
        var invoiceDetails = [...props.invoiceDetails];
        invoiceDetails[rowIndex].showDetail = showDetail;
        props.beforeValid(invoiceDetails);
    }

    const onCellChange = (invIndex: number, itemIndex: number, item: string, value: any) => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];
        invoice.isDirty = true; //means at least one of the invoice items has changed
        var lastrow = invoice.items.length - 1 == itemIndex;
        var invoiceItem = invoice.items[itemIndex];
        let newInvoiceItem: any = {};
        newInvoiceItem = invoiceItem;
        newInvoiceItem[item] = value;
        invoiceItem.isDirty = true;

        var invoiceTotal = 0;
        if (item == "amount") {
            var chargeGst = invoiceItem.chargeGst ? invoiceItem.chargeGst : true;

            if (chargeGst) {
                var gstToCalc = gst + 1;
                var rate = value / gstToCalc;
                invoiceItem.rate = (rate).toLocaleString("en-AU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
                var gstAmount = value - rate;
                invoiceItem.gst = (gstAmount).toLocaleString("en-AU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
            } else {
                invoiceItem.rate = (value).toLocaleString("en-AU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
                invoiceItem.gst = "0.00";
            }

            //recalc total
            for (var i = 0; i < invoice.items.length - 1; i++) {
                if (invoice.items[i].status == 1) {
                    //only include non cancelled items
                    //invoiceTotal += parseInt(invoice.invoiceItems[i].amount, 10);
                    invoiceTotal += parseFloat(invoice.items[i].amount);
                }
            }
            invoice.amount = (invoiceTotal).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        } else if (item == "invoiceStartDate" || item == "invoiceEndDate") {
            //var start = item == "invoiceStartDate" ? value : invoiceItem.invoiceStartDate;
            //var end = item == "invoiceEndDate" ? value : invoiceItem.invoiceEndDate;
            //need to calculate no of days
            invoiceItem.hirePeriodDays = (DateHelpers.daysBetweenDates(invoiceItem.invoiceStartDate, invoiceItem.invoiceEndDate) + 1) + " Days";
        }

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && props.canEdit && !invoice.reckonInvoiceId) {
            var blankItem = addBlankItem(invoice.items.length, invoice.id)
            invoice.items.push(blankItem);
        }

        props.beforeValid(invoices);
    }

    const addBlankItem = (nextItemIndex: number, jobInvId: number) => {
        var blankItem = {
            itemIndex: nextItemIndex,
            id: 0,
            jobInvoiceId: jobInvId,
            branchIdent: "",
            item: "",
            itemId: -1,
            itemTypeId: -1,
            invItemType: "",
            invItemTypeId: 0,
            quantityType: "",
            purchaseOrderNumber: "",
            additionalHireRate: "",
            rate: "",
            rateFormatted: "",
            gst: "",
            gstFormatted: "",
            chargeGst: true,
            amount: "",
            amountFormatted: "",
            description: "",
            status: 1,
            invoiceStartDate: "",
            invoiceEndDate: "",
            invoiceStartDateFormatted: "",
            invoiceEndDateFormatted: "",
            hirePeriodDays: "",
            hirePeriod: "",
            dayRate: "",
            weekMonthRate: "",
            isDirty: false
        };

        return blankItem;
    }

    //DATE POPUP 
    const saveDateModal = () => {
        savePaidDate();
    }

    const hideDateModal = () => {
        setModalType(0);
        setShowDateModal(false);
        setModalDate("");
        setInvoiceIndex(0);
        setInvoiceNumber(0);
    }

    const updateDateChangeModal = (date: string) => {
        setModalDate(date);
        setErrors({});
    }
    //END DATE POPUP

    //CONFIRM POPUP
    const saveConfirmModal = () => {
        if (modalType == 1) {
            cancelInvoiceSave();
        } else if (modalType == 4) {
            removeInvoiceSave();
        } else if (modalType == 5) {
            cancelInvoiceItemSave();
        }
    }

    const hideConfirmModal = () => {
        setModalType(0);
        setInvoiceIndex(0);
        setInvoiceNumber(0);
        setShowConfirmModal(false);
    }
    //END CONFIRM POPUP

    //REMOVE INVOICE FROM BATCH
    const removeInvoice = (e: any, invIndex: number) => {
        e.preventDefault();
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];

        setModalType(4);
        setInvoiceIndex(invIndex);
        setInvoiceNumber(invoice.id);
        setShowConfirmModal(true);
    }

    const removeInvoiceSave = () => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invoiceIndex];
        if (invoice) {
            saveRemoveInvoice(invoices, invoice);
        }
    }

    const saveRemoveInvoice = async (invoices: InvoiceDetails[], invoice: InvoiceDetails) => {
        var invoiceToRemove = {
            InvoiceId: invoice.id
        };

        var jsonItem = JSON.stringify(invoiceToRemove);

        const apiUrl = "RemoveInvoiceFromBatch";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            setModalType(0);
            setInvoiceIndex(0);
            setInvoiceNumber(0);
            setShowConfirmModal(false);

            if (result.data == true) {
                toast.success("Invoice Removed from Batch");
                invoices.splice(invoiceIndex, 1);

                props.beforeValid(invoices);
            } else {
                toast.error("There was an error removing the invoice, please refresh and try again!");
            }
        }
    }
    //END REMOVE INVOICE FROM BATCH

    //CANCEL INVOICE
    const cancelInvoice = (e: any, invIndex: number) => {
        e.preventDefault();
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];
        setModalType(1);
        setInvoiceIndex(invIndex);
        setInvoiceNumber(invoice.id);
        setShowConfirmModal(true);
    }

    const cancelInvoiceSave = () => {
        saveCancelInvoice();
    }

    const saveCancelInvoice = async () => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invoiceIndex];
        if (invoice) {
            invoice.status = "Cancelled";
            invoice.statusId = 4;
            //remove blank item row
            invoice.items.splice(invoice.items.length - 1, 1);

            var invoiceToCancel = {
                InvoiceId: invoice.id
            };

            try {
                var jsonItem = JSON.stringify(invoiceToCancel);

                const apiUrl = "CancelInvoice";
                var result = await PostQuery(apiUrl, jsonItem);
                if (result.error) {
                    toast.error("Error saving data!");
                } else {
                    setModalType(0);
                    setInvoiceIndex(0);
                    setInvoiceNumber(0);
                    setShowConfirmModal(false);

                    if (result.data == true) {
                        toast.success("Invoice Cancelled");

                        props.beforeValid(invoices);
                    } else {
                        toast.error("There was an error cancelling the invoice, please refresh and try again!");
                    }
                }
            } catch (e) {
                toast.error("Error cancelling invoice!");
            }
        }
    }

    const cancelInvoiceItem = (e: any, invIndex: number, itemIndex: number) => {
        e.preventDefault();
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];
        var noRows = invoice.items.filter(data => data.status == 1).length;  //filter out the cancelled items as not shown in grid, so we know if last row
        if (noRows > 2) {
            setModalType(5);
            setInvoiceIndex(invIndex);
            setInvoiceItemIndex(itemIndex);
            setInvoiceNumber(invoice.id);
            setShowConfirmModal(true);
        } else {
            //can't delete only line
            toast.error("Cannot delete only row. Add a new row before deleting current row.");
        }
    }

    const cancelInvoiceItemSave = () => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invoiceIndex];
        var invoiceItem = invoice.items[invoiceItemIndex];

        if (invoiceItem) {
            invoiceItem.status = 0;
            if (invoiceItem.id > 0) {
                saveCancelInvoiceItem(invoices, invoice, invoiceItem);
            }  
        }
        
    }

    const saveCancelInvoiceItem = async (invoices: InvoiceDetails[], invoice: InvoiceDetails, invoiceItem: InvoiceItems) => {
        var itemToCancel = {
            InvoiceItemId: invoiceItem.id
        };

        try {
            var jsonItem = JSON.stringify(itemToCancel);

            const apiUrl = "CancelInvoiceItem";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                setModalType(0);
                setInvoiceIndex(0);
                setInvoiceNumber(0);
                setShowConfirmModal(false);

                if (result.data == true) {
                    var invoiceTotal = 0;
                    //recalc total
                    for (var i = 0; i < invoice.items.length - 1; i++) {
                        if (invoice.items[i].status == 1) {
                            //only include non cancelled items
                            invoiceTotal += parseInt(invoice.items[i].amount, 10);
                        }
                    }
                    invoice.amount = (invoiceTotal).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                    props.beforeValid(invoices);

                    toast.success("Item Cancelled");
                } else {
                    toast.error("There was an error cancelling the item, please refresh and try again!");
                }
            }
        } catch (e) {
            toast.error("Error cancelling invoice item!");
        }
    }

    //END CANCEL INVOICE

    //SET INVOICE TO SENT - GET QUICK BOOKS INVOICE NO
    const invoiceSent = (e: any, invIndex: number) => {
        e.preventDefault();
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];

        setInvoiceIndex(invIndex)
        setInvoiceNumber(invoice.id);
        setShowSentModal(true);
    }

    const hideSentModal = () => {
        setInvoiceIndex(0);
        setInvoiceNumber(0);
        setShowSentModal(false);
        setQbInvoiceNumber(0);
        setErrors({});
    }

    const handleModalSentChange = (e: any) => {
        setQbInvoiceNumber(e.target.value * 1);
        setErrors({});
    }

    const validateSentModal = () => {
        let errorList: any = {};
        let formIsValid = true;

        if(!qbinvoicenumber) {
            errorList["modalSent"] = "Enter the Quick Books Invoice Number";
            formIsValid = false;
            setErrors(errorList);
        }

        return formIsValid;
    }

    const saveSentModal = () => {
        if (validateSentModal()) {
            updateInvoiceToSent();
        }
    }

    const updateInvoiceToSent = async () => {
        //invoice has been sent to Reckon, so just update in Dash to Sent
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invoiceIndex];

        var sentDetails = {
            invoiceId: invoice.id,
            invoiceNumber: qbinvoicenumber
        };

        try {
            var jsonItem = JSON.stringify(sentDetails);

            const apiUrl = "UpdateInvoiceToSent";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                /*update status to sent*/
                invoice.status = "SentToReckon";
                invoice.statusId = 6;
                props.beforeValid(invoices);

                toast.success("Invoice Saved as Sent To Reckon");
                setInvoiceIndex(0);
                setInvoiceNumber(0);
                setShowSentModal(false);
                setQbInvoiceNumber(0);
                setErrors({});
            }
        } catch (e) {
            toast.error("Error sending invoice to Reckon!");
        }
    }

    //SET INVOICE TO SENT END

    //SEND INVOICE
    const sendInvoice = (e: any, invIndex: number) => {
        e.preventDefault();
        setInvoiceIndex(invIndex);
        updateStatusToSending(invIndex);
    }

    const updateStatusToSending = async (invIndex: number) => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];

        var updateInvoiceToSending = {
            invoiceId: invoice.id,
        };

        try {
            var jsonItem = JSON.stringify(updateInvoiceToSending);

            const apiUrl = "UpdateInvoiceToSending";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    for (var i = 0; i < errors.length; i++) {
                        toast.error(errors[i].content);
                    };
                } else {
                    //update the status on the screen
                    invoice.status = "Sending";
                    invoice.statusId = 7;
                    props.beforeValid(invoices);

                    saveSendInvoice(invIndex);
                }
            }
        } catch (e) {
            toast.error("Error updating invoice to sending status!");
            setModalType(0);
            setInvoiceIndex(0);
            setShowDateModal(false);
            setModalDate("");
            props.beforeValid(invoices);
        }
    }

    const saveSendInvoice = async (invIndex: number) => {
        const baseURL = window.location.origin;
        const apiURL = new URL('api/SendInvoice', baseURL);

        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];
       
        var sendInvoice = {
            invoiceId: invoice.id,
            invoiceDate: modalDate,
            companyId: invoice.companyId,                   //job company id
            invoiceCompanyId: invoice.invoiceCompanyId     //inv company id
        };

        try {
            var jsonItem = JSON.stringify(sendInvoice);

            const apiUrl = "SendInvoice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                setModalType(0);
                setInvoiceIndex(0);
                setShowDateModal(false);
                setModalDate("");

                let errMsg = "";
                if (result.data.success) {
                    toast.success("Invoice Saved as Sent");
                } else {
                    toast.error("Error while sending invoice: " + result.data.errorMessage);
                    errMsg = result.data.errorMessage;
                }

                //update invoice status
                invoice.status = result.data.status;
                invoice.statusId = result.data.statusId;
                invoice.reckonInvoiceId = result.data.reckonInvoiceId;
                if (errMsg) {
                    invoice.errorMessage = errMsg;
                }
                props.beforeValid(invoices);
            }
        } catch (e) {
            toast.error("Error sending invoice to Reckon!");
            setModalType(0);
            setInvoiceIndex(0);
            setShowDateModal(false);
            setModalDate("");

            resetStatusToCreated(invIndex);
        }
    }

    //this only happens if the back-end throws an error as I catch all the errors from Reckon and save the invoice as error status
    const resetStatusToCreated = async (invIndex: number) => {
        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invIndex];

        var resetInvoice = {
            invoiceId: invoice.id
        };

        try {
            var jsonItem = JSON.stringify(resetInvoice);

            const apiUrl = "UpdateInvoiceToCreated";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                //update the status on the screen
                invoice.status = "Created";
                invoice.statusId = 1;
                props.beforeValid(invoices);
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }

    //END SEND INVOICE

    //PAID INVOICE
    const paid = (e: any, invIndex: number) => {
        e.preventDefault();
        var invoices = props.invoiceDetails;
        var invoice = invoices[invIndex];
        setModalType(3);
        setInvoiceIndex(invIndex);
        setInvoiceNumber(invoice.id);
        setShowDateModal(true);
    }

    const savePaidDate = async () => {
        const baseURL = window.location.origin;
        const apiURL = new URL('api/SaveInvoicePaidDate', baseURL);

        var invoices = [...props.invoiceDetails];
        var invoice = invoices[invoiceIndex];
        invoice.paidDate = DateHelpers.formatDate(modalDate);
        invoice.paidDateActual = modalDate;
        invoice.status = "Paid";
        invoice.statusId = 3;

        var invoiceDetais = {
            id: invoice.id,
            paidDate: modalDate
        };

        try {
            var jsonItem = JSON.stringify(invoiceDetais);

            const apiUrl = "SaveInvoicePaidDate";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                //save invoice
                setModalType(0);
                setInvoiceIndex(0);
                setInvoiceNumber(0);
                setShowDateModal(false);
                setModalDate("");
                setErrors({});

                props.beforeValid(invoices);
                toast.success("Paid Date Saved");
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }
    //END PAID INVOICE

    const pageChanged = (page: number) => {
        props.pageChanged(page);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        let invoiceNoErrors = [];

        //Only check invoices that have changed
        let invoices = [...props.invoiceDetails.filter(x => x.isDirty)];
        for (var i = 0; i < invoices.length; i++) {
            let items = invoices[i].items;
            var rowNumber = 0;

            var errorString = "Please enter the following values: ";
            var error = false;
            //last row is a blank new row so ignore
            for (var y = 0; y < items.length - 1; y++) {
                rowNumber = y + 1;
                //ignore any cancelled items and anything that hasn't been changed
                if (items[y].status == 1 && items[y].isDirty) {
                    var newErrorString = "";
                    if (items[y].invItemTypeId < 1) {
                        newErrorString += " Type, ";
                        error = true;
                    }
                    if (items[y].id == 0 && !items[y].itemId) {
                        newErrorString += " Item, ";
                        error = true;
                    }
                    if (items[y].description == "") {
                        newErrorString += " Description, ";
                        error = true;
                    }
                    if (items[y].invoiceStartDate == "") {
                        newErrorString += " Start Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[y].invoiceStartDate)) {
                        newErrorString += " Valid Start Date, ";
                        error = true;
                    }
                    if (items[y].invoiceEndDate == "") {
                        newErrorString += " End Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[y].invoiceEndDate)) {
                        newErrorString += " Valid End Date, ";
                        error = true;
                    }
                    if (items[y].amount == "") {
                        newErrorString += " Amount, ";
                        error = true;
                    }
                    
                    if (newErrorString != "") {
                        errorString += "\n"
                        errorString += "Row " + rowNumber + ": " + newErrorString;
                        errorString = errorString.substring(0, errorString.length - 2);
                    }
                }

            }
            if (error) {
                invoiceNoErrors.push(invoices[i].id)
                formIsValid = false;
                invoices[i].errors = {};
                invoices[i].errors["invoiceItems"] = errorString;
            }
        }
        if (invoiceNoErrors.length > 0) {
            var errormessage = "Please fix errors on the following invoice numbers: ";
            for (var z = 0; z < invoiceNoErrors.length; z++) {
                errormessage += invoiceNoErrors[z] + " ";
            }
            errorList["invoiceItems"] = errormessage;
            props.beforeValid(invoices);
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            //only save invoices that have changed
            let invoices = [...props.invoiceDetails.filter(x => x.isDirty)];
            if (invoices.length > 0) {
                props.afterValid(invoices);
            } else {
                toast.info("No invoices have been changed");
            }
        }
    }

    const validateHirePeriod = () => {
        let errorList: any = {};
        let formIsValid = true;
        let invoiceNoErrors = [];

        //Only check invoices that have changed
        let invoices = [...props.invoiceDetails.filter(x => x.isDirty)];
        for (var i = 0; i < invoices.length; i++) {
            let items = invoices[i].items;
            var rowNumber = 0;

            var errorString = "Please enter the following values: ";
            var error = false;

            for (var y = 0; y < items.length; y++) {
                rowNumber = y + 1;
                //ignore any cancelled items and anything that hasn't been changed
                if (items[y].status == 1 && items[y].isDirty) {
                    var newErrorString = "";
                    if (items[y].invoiceStartDate == "") {
                        newErrorString += " Start Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[y].invoiceStartDate)) {
                        newErrorString += " Valid Start Date, ";
                        error = true;
                    }
                    if (items[y].invoiceEndDate == "") {
                        newErrorString += " End Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[y].invoiceEndDate)) {
                        newErrorString += " Valid End Date, ";
                        error = true;
                    }

                    if (newErrorString != "") {
                        errorString += "\n"
                        errorString += "Row " + rowNumber + ": " + newErrorString;
                        errorString = errorString.substring(0, errorString.length - 2);
                    }
                }

            }
            if (error) {
                invoiceNoErrors.push(invoices[i].id)
                formIsValid = false;

                invoices[i].errors["invoiceItems"] = errorString;
            }
        }
        if (invoiceNoErrors.length > 0) {
            var errormessage = "Please fix errors on the following invoice numbers: ";
            for (var z = 0; z < invoiceNoErrors.length; z++) {
                errormessage += invoiceNoErrors[z] + " ";
            }
            errorList["invoiceItems"] = errormessage;
            props.beforeValid(invoices);
        }
        setErrors(errorList);
        return formIsValid;
        
    }

    const saveHirePeriod = () => {
        if (validateHirePeriod()) {
            //only save invoices that have changed
            let invoices = [...props.invoiceDetails.filter(x => x.isDirty)];
            if (invoices.length > 0) {
                props.saveHirePeriod(invoices);
            } else {
                toast.info("No invoices have been changed");
            }
        }
    }

    //work out how many columns the sub grid of items should span
    var itemGridColSpan = 8;
    if (props.showCustType) { itemGridColSpan += 1 }
    if (props.showJobInfo) { itemGridColSpan += 2 }
    if (props.showQuoteNo) { itemGridColSpan += 1 }
    if (props.showHirePeriod) { itemGridColSpan += 1 }
    if (props.showPaidDate) { itemGridColSpan += 1 }
    if (props.showStatus) { itemGridColSpan += 1 }
    if (props.showPaid) { itemGridColSpan += 1 }
    if (props.showSend) { itemGridColSpan += 1 }
    if (props.showRemove) { itemGridColSpan += 1 }
    if (props.showSentTo) { itemGridColSpan += 1 }
    if (props.showReckonInvNo) { itemGridColSpan += 1 }
    if (props.showError) { itemGridColSpan += 1 }
    if (props.showSent) { itemGridColSpan += 1 }

    var pagerContents;
    if (props.invoiceDetails.length > 0) {
        //only render if we have records
        pagerContents = <Pager page={props.page} pageSize={props.pageSize} totalRecords={props.totalRecords} pageChanged={pageChanged} />
    }

    //confirm popup for cancelling invoice
    var heading = modalType == 0 ? "" : modalType == 1 ? "Cancel Invoice" : modalType == 5 ? "Delete Invoice Item" : "Remove Invoice from Batch";
    var text = modalType == 0 ? "" : modalType == 1 ? "Are you sure you want to cancel invoice " + invoiceNumber + "?" : modalType == 5 ? "Are you sure you want to delete this invoice item?" : "Are you sure you want to remove this Invoice from the Batch?";
    let confirmPopup = <ConfirmPopup heading={heading} text={text} hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />
    let paidDateModal = <InvoicingPaidDateModal canUpdatePaid={props.canUpdatePaid} date={modalDate} showModal={showDateModal} hideModal={hideDateModal} update={updateDateChangeModal} save={saveDateModal} />

    const renderDetails = (
        <div>
            <div>
                <div className="static-modal">
                    {paidDateModal}
                </div>
                <div className="static-modal">
                    <Modal show={showSentModal} onHide={hideSentModal}>
                        <Modal.Header className="modal__header--colour">
                            <Modal.Title>Enter Reckon Invoice Number</Modal.Title>
                            <button type="button" className="close" onClick={hideSentModal}>&times;</button>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                <label className="input-group" htmlFor="qbinvoicenumber">
                                    <span className="label">Invoice Number</span>
                                    <input type='number' id="qbinvoicenumber" name="qbinvoicenumber" className='input' value={qbinvoicenumber} onChange={(e) => handleModalSentChange(e)}></input>
                                </label>
                                <span className="label errors">{errors["modalSent"]}</span>
                            </div>
                        </Modal.Body>

                        <Modal.Footer>
                            <button className="popup-button--yes" onClick={saveSentModal}>Save</button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <div className="static-modal">
                    {confirmPopup}
                </div>
                <div className="table__margin overflowAuto">
                    <table className="table table__large tableColours">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Inv #</th>
                                <th>Batch #</th>
                                <th>Customer</th>
                                <th className={props.showCustType ? "" : "hidden"}>Cust Type</th>
                                <th className={props.showJobInfo ? "" : "hidden"}>Job Id</th>
                                <th className={props.showJobInfo ? "" : "hidden"}>Site Address</th>
                                <th>Type</th>
                                <th className={props.showSentTo ? "" : "hidden"}>Sent To</th>
                                <th className={props.showReckonInvNo ? "" : "hidden"}>Reckon Inv #</th>
                                <th className={props.showQuoteNo ? "" : "hidden"}>Quote #</th>
                                <th className={props.showStartDate ? "" : "hidden"}>Min Hire Start</th>
                                <th>Inv Date</th>
                                <th className="hidden">Due Date</th>
                                <th className={props.showPaidDate ? "" : "hidden"}>Paid Date</th>
                                <th>Amount</th>
                                <th className={props.showStatus ? "" : "hidden"}>Status</th>
                                <th className={props.showPaid ? "" : "hidden"}></th>
                                <th className={props.showError ? "" : "hidden"}></th>
                                <th className={props.showSend ? "" : "hidden"}></th>
                                <th className={props.showSent ? "" : "hidden"}></th>
                                <th className={props.showRemove ? "" : "hidden"}>New Batch</th>
                                <th></th>
                            </tr>
                        </thead>
                        {props.invoiceDetails.map((invoice, index) =>
                            <tbody key={invoice.id}>
                                <tr>
                                    <td className="table__text--align"><button type="button" className="btn btn-default btn-sm" onClick={() => updateShowDetail(index, invoice.showDetail)}><i className={"fas fa-plus" + (invoice.showDetail ? ' rotate45deg' : '')}></i></button></td>
                                    <td className="table__text--align">{invoice.id}</td>
                                    <td className="table__text--align">{invoice.batchNumber}</td>
                                    <td className="table__text--align">{invoice.customer}</td>
                                    <td className={props.showCustType && invoice.customerType === "Prepaid" ? "table__text--align makeitred" : props.showCustType ? "table__text--align" : "hidden"}>
                                        {invoice.customerType}
                                    </td>
                                    <td className={props.showJobInfo ? "table__text--align" : "hidden"}>
                                        <Link to={'/job/' + invoice.jobId + '/details'} target="_blank">
                                            {invoice.jobId}
                                        </Link>
                                    </td>
                                    <td className={props.showJobInfo ? "table__text--align" : "hidden"}>{invoice.siteAddress}</td>
                                    <td className="table__text--align">
                                        {invoice.type}
                                    </td>
                                    <td className={props.showSentTo ? "table__text--align" : "hidden"}>
                                        <div className="tooltipitem tooltipitem__right">{invoice.sendTo ? invoice.sendTo.substring(0, 30) + "... " : ""}
                                            <span className="tooltipitemtext tooltipitemtext__right">{invoice.sendTo}</span>
                                        </div>
                                    </td>
                                    <td className={props.showReckonInvNo ? "table__text--align" : "hidden"}>
                                        {invoice.reckonInvoiceId}
                                    </td>
                                    <td className={props.showQuoteNo ? "table__text--align" : "hidden"}>{invoice.quoteNumber}</td>
                                    <td className={props.showStartDate ? "table__text--align" : "hidden"}>{invoice.invoiceStartDateFormatted}</td>
                                    <td className="table__text--align">{invoice.invoiceDateFormatted}</td>
                                    <td className="hidden">{invoice.dueDate}</td>
                                    <td className={props.showPaidDate ? "table__text--align" : "hidden"}>{invoice.paidDateFormatted}</td>
                                    <td className="table__text--align">{invoice.amount}</td>
                                    <td className={props.showStatus ? "table__text--align" : "hidden"}>{invoice.status}</td>
                                    <td className={props.showPaid ? "table__text--align" : "hidden"}>
                                        <button className={invoice.statusId == 2 && invoice.paidDate == "" && props.canUpdatePaid && props.showPaid ? "btn btn-primary" : "hidden"} type="button" onClick={(e) => paid(e, index)}>Paid</button>
                                    </td>
                                    <td className={props.showError ? "table__text--align" : "hidden"}>{invoice.errorMessage}</td>
                                    <td className={props.showSend ? "table__text--align" : "hidden"}>
                                        <button className={(invoice.statusId == 1 || (invoice.statusId == 5 && canResend)) && props.canSend && props.showSend && invoice.status != "Sending" ? "btn btn-primary" : "hidden"} type="button" onClick={(e) => sendInvoice(e, index)} disabled={invoice.isDirty}>{invoice.statusId == 5 ? "Re-send" : "Send"} </button>
                                        {(invoice.statusId == 1 || invoice.statusId == 5) && props.canSend && props.showSend && invoice.status != "Sending" ? "" : props.showStatus ? "" : invoice.status}
                                    </td>
                                    <td className={props.showSent ? "table__text--align" : "hidden"}>
                                        <button className={invoice.statusId == 5 && props.canSend && props.showSent ? "btn btn-success" : "hidden"} type="button" onClick={(e) => invoiceSent(e, index)}>Sent</button>
                                    </td>
                                    <td className={props.showRemove ? "table__text--align" : "hidden"}>
                                        <div className={props.canEdit && (invoice.statusId === 2 || invoice.statusId === 3) ? "delete--tablecell" : "hidden"}>
                                            <a className="makeitred" href="#" onClick={(e) => removeInvoice(e, index)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                    <td className="table__text--align">
                                        {invoice.canDelete && props.canEdit && !props.showRemove ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => cancelInvoice(e, index)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr>
                                <tr className={(invoice.showDetail ? '' : 'row--hidden')}>
                                    <td colSpan={itemGridColSpan}>
                                        <div className="overflowAuto">
                                            <table className='table subtable subtable__large tableColours'>
                                                <thead>
                                                    <tr>
                                                        <th className="hidden">Id</th>
                                                        <th>Type</th>
                                                        <th></th>
                                                        <th>Item</th>
                                                        <th>Total</th>
                                                        <th>P/O # </th>
                                                        <th>Description</th>
                                                        <th>Hire Start</th>
                                                        <th>Hire End</th>
                                                        <th>{invoice.companyId === 3 ? "Hire Period" : "Days Hire"}</th>
                                                        <th>{invoice.companyId === 3 ? "Rate" : "Day Rate"}</th>
                                                        <th className="hidden">Rate</th>
                                                        <th className="hidden">GST</th>
                                                        <th>Amount</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                {invoice.items.map((item, itemIndex) =>
                                                    <tbody key={itemIndex}>
                                                        <tr className={item.status == 0 ? "hidden" : ""}>
                                                            <td className="hidden">{item.id}</td>
                                                            <td className="table__text--align">{invoice.canEdit && props.canEdit ? "" : item.invItemType}
                                                                <select className={invoice.canEdit && props.canEdit ? "select table__select--size" : "hidden"} id="invItemTypeId" name="invItemTypeId" value={item.invItemTypeId} onChange={(e) => onCellChange(index, item.itemIndex, "invItemTypeId", Number(e.target.value))} >
                                                                    <option hidden defaultValue="-1"></option>
                                                                    {itemTypes.map(type =>
                                                                        <option key={type.id} value={type.id}>{type.name}</option>
                                                                    )};
                                                                </select>
                                                            </td>
                                                            <td className="table__text--align">
                                                                {item.branchIdent}
                                                            </td>
                                                            <td className="table__text--align">
                                                                <select className={invoice.canEdit && props.canEdit && item.id == 0 ? "select table__select--size" : "hidden"} id="itemId" name="itemId" value={item.itemId} onChange={(e) => onCellChange(index, item.itemIndex, "itemId", Number(e.target.value))} >
                                                                    <option hidden defaultValue="-1"></option>
                                                                    {itemList.map(type =>
                                                                        <option key={type.id} value={type.id}>{type.name}</option>
                                                                    )};
                                                                </select>
                                                                {invoice.canEdit && props.canEdit && item.id == 0 ? "" : item.item}
                                                            </td>
                                                            <td className="table__text--align">
                                                                {invoice.type === "Callout" ? "" : item.quantityType}
                                                            </td>
                                                            <td className="table__text--align">{invoice.canEdit && props.canEdit ? "" : item.purchaseOrderNumber}
                                                                <input type="text" maxLength={25} className={invoice.canEdit && props.canEdit ? "form-control invoicing__poSize" : "hidden"} value={item.purchaseOrderNumber} onChange={(e) => onCellChange(index, item.itemIndex, "purchaseOrderNumber", e.target.value)} />
                                                            </td>
                                                            <td className="table__text--align">{invoice.canEdit && props.canEdit ? "" : item.description}
                                                                <input type="text" className={invoice.canEdit && props.canEdit ? "form-control" : "hidden"} value={item.description} onChange={(e) => onCellChange(index, item.itemIndex, "description", e.target.value)} />
                                                            </td>
                                                            <td className="table__text--align">
                                                                {(props.canEdit || props.canEditHirePeriod) && invoice.statusId != 4 ? "" : item.invoiceStartDateFormatted}
                                                                <input type="date" className={(props.canEdit || props.canEditHirePeriod) && invoice.statusId != 4 ? "form-control" : "hidden"} value={item.invoiceStartDate} onChange={(e) => onCellChange(index, item.itemIndex, "invoiceStartDate", e.target.value)} />
                                                            </td>
                                                            <td className="table__text--align">
                                                                {(props.canEdit || props.canEditHirePeriod) && invoice.statusId != 4 ? "" : item.invoiceEndDateFormatted}
                                                                <input type="date" className={(props.canEdit || props.canEditHirePeriod) && invoice.statusId != 4 ? "form-control" : "hidden"} value={item.invoiceEndDate} onChange={(e) => onCellChange(index, item.itemIndex, "invoiceEndDate", e.target.value)} />
                                                            </td>
                                                            <td className="table__text--align">{invoice.companyId === 3 ? item.hirePeriod : item.hirePeriodDays}</td>
                                                            <td className="table__text--align">{(invoice.type == "Overhire" || invoice.type == "Final") ? invoice.companyId === 3 ? item.weekMonthRate : item.dayRate : ""}</td>
                                                            <td className="hidden">{invoice.canEdit && props.canEdit ? "" : item.rateFormatted}
                                                                <input type='number' min="0" step="0.01" className={invoice.canEdit && props.canEdit ? "form-control table__input--alignright table__input--min100" : "hidden"} value={item.rate} onChange={(e) => onCellChange(index, item.itemIndex, "rate", Number(e.target.value))} />
                                                            </td>
                                                            <td className="hidden">{invoice.canEdit && props.canEdit ? "" : item.gstFormatted}
                                                                <input type='number' min="0" step="0.01" className={invoice.canEdit && props.canEdit ? "form-control table__input--alignright table__input--min100" : "hidden"} value={item.gst} onChange={(e) => onCellChange(index, item.itemIndex, "gst", Number(e.target.value))} />
                                                            </td>
                                                            <td className="table__text--align table__input--alignright">{invoice.canEdit && props.canEdit ? "" : item.amountFormatted}
                                                                <input type='number' min="0" step="0.01" className={invoice.canEdit && props.canEdit ? "form-control table__input--alignright table__input--min100" : "hidden"} value={item.amount} onChange={(e) => onCellChange(index, item.itemIndex, "amount", Number(e.target.value))} />
                                                            </td>
                                                            <td className="table__text--align">
                                                                {(invoice.canEdit && itemIndex != (invoice.items.length - 1)) && props.canEdit ?
                                                                    <div className="delete--tablecell">
                                                                        <a className="makeitred" href="#" onClick={(e) => cancelInvoiceItem(e, index, item.itemIndex)}>
                                                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                                        </a>
                                                                    </div> : ""
                                                                }
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                )}
                                            </table>
                                            {invoice.errors && invoice.errors["invoiceItems"] ?
                                                (invoice.errors["invoiceItems"]).split("\n").map((item: any, key: any) => {
                                                    return <span className="label errors" key={key}>{item}<br /></span>
                                                })
                                                : ""
                                            }
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    <div className={props.invoiceDetails.length > 0 ? "colAlignRight" : "hidden"}>
                        <div className="invoicing__total--large">Total {props.invoiceTotalFormatted}</div>
                    </div>
                    <span className="label errors">{errors["invoiceItems"]}</span>
                </div>
                {pagerContents}
                <div className={props.canEdit && props.invoiceDetails.length > 0 ? "colAlignRight" : "hidden"}>
                    <button className="defaultbutton marginBottom10" type="button" onClick={save}>Save</button>
                </div>
                <div className={!props.canEdit && props.canEditHirePeriod ? "colAlignRight" : "hidden"}>
                    <button className="defaultbutton marginBottom10" type="button" onClick={saveHirePeriod}>Save Hire Period</button>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default InvoiceListData;


